import { CaretRightIcon } from '@phosphor-icons/react'
import { DialogClose } from '@radix-ui/react-dialog'
import axios from 'axios'
import Image from 'next/image'
import { useMemo, useState } from 'react'
import useSWR from 'swr'
import { MyBLINK } from '@/src/@types/blinkadmin'
import { fullFlchUrl } from '@/src/BlinkAdminApiClient'
import Button from '@/src/components/core/Button'
import { DialogBody, DialogHeader } from '@/src/components/core/Dialog'
import Picture from '@/src/components/elements/Picture'
import { useBookingContext } from '@/src/components/flchBooking/locationContext/BookingContext'
import AllInstructorsPicture from '@/src/components/flchBooking/locationContext/overview/all_instructors.jpg'
import Profile from '@/src/components/flchBooking/locationContext/overview/instructors/Profile'

export default function Instructors() {
  const { location, gearbox, confirmInstructors } = useBookingContext()

  const [selectedInstructor, setSelectedInstructor] =
    useState<MyBLINK.BookableUser | null>(null)

  const { data: locationInstructors, isLoading } = useSWR<
    MyBLINK.BookableUser[]
  >(
    location && `booking/locations/${location.id}/users?gearbox=${gearbox}`,
    async () =>
      (
        await axios.get(
          fullFlchUrl(
            `booking/locations/${location!.id}/users?gearbox=${gearbox}`
          )
        )
      ).data
  )

  const availableInstructors = useMemo(() => {
    if (!locationInstructors) {
      return null
    }
    if (gearbox === 'automatic') {
      return locationInstructors.filter(
        (instructor) => instructor.gearboxAvailability.hasAutomatic
      )
    }
    if (gearbox === 'manual') {
      return locationInstructors.filter(
        (instructor) => instructor.gearboxAvailability.hasManual
      )
    }
    return locationInstructors
  }, [locationInstructors, gearbox])

  if (selectedInstructor) {
    return (
      <Profile
        instructor={selectedInstructor}
        confirm={() => confirmInstructors([selectedInstructor])}
        deselect={() => setSelectedInstructor(null)}
      />
    )
  }

  return (
    <>
      <DialogHeader title="Mit wem möchtest du fahren?" />
      <DialogBody>
        {isLoading && !availableInstructors && (
          <p className="flex items-center justify-center space-x-2 py-2">
            <svg
              className="size-4 animate-spin text-blue-800"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <span>Lade Fahrlehrer:innen...</span>
          </p>
        )}
        {availableInstructors && (
          <>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              {availableInstructors.map((instructor) => (
                <button
                  onClick={() => setSelectedInstructor(instructor)}
                  className="flex flex-col justify-between rounded-md border border-gray-150 bg-gray-25"
                >
                  <div className="aspect-w-4 relative w-full overflow-hidden aspect-h-3">
                    <Picture
                      permalink={instructor.photo}
                      alt={instructor.name}
                      fill
                      sizes="180px"
                      className="object-cover object-top"
                    />
                  </div>
                  <div className="flex w-full items-center justify-between py-2 pr-2 pl-3">
                    <p className="text-left">
                      {instructor.name}{' '}
                      {instructor.id === 'o1bqdi' ? '(Aushilfe)' : null}
                    </p>
                    <CaretRightIcon
                      size={20}
                      weight="bold"
                      className="text-blue-700"
                    />
                  </div>
                </button>
              ))}
              {availableInstructors.length !== 1 && (
                <button
                  onClick={() => confirmInstructors(availableInstructors)}
                  className="rounded-sm border border-gray-150 bg-gray-25"
                >
                  <div className="aspect-w-4 w-full aspect-h-3">
                    <Image
                      src={AllInstructorsPicture}
                      alt="Alle Fahrlehrer:innen"
                      fill
                      className="object-cover object-top"
                      unoptimized
                    />
                  </div>
                  <div className="flex w-full items-center justify-between py-2 pr-2 pl-3">
                    <p className="text-left">Mir ist es egal.</p>
                    <CaretRightIcon
                      size={20}
                      weight="bold"
                      className="text-blue-700"
                    />
                  </div>
                </button>
              )}
            </div>
            <div className="mt-8 flex justify-end space-x-4">
              <DialogClose asChild>
                <Button version="gray-transparent">Abbrechen</Button>
              </DialogClose>
            </div>
          </>
        )}
      </DialogBody>
    </>
  )
}
